import React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { RiskFlag, RiskFlagCategory } from "@/lib/api/risk-flagging";
import { useShareableEntity } from "@/lib/api/shareable-entities";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { MutingAudio, MutingVideo } from "@/components/shared/muting-media";
import { isS3Url, getMediaType } from "./utils";
import { PriorityPill } from "./pills";
import {
  ACTIVITY_TYPE_LABELS,
  CATEGORY_LABELS,
  PROFILE_FIELD_LABELS,
  SOURCE_TYPE_LABELS,
} from "./constants";

export function DrawerField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs text-[#58534c]">{label}</p>
      <div className="text-sm text-[#1f1e1d]">{children}</div>
    </div>
  );
}

export function Divider() {
  return <div className="h-px w-full bg-[rgba(31,30,29,0.12)]" />;
}

export function AlertInfo({ alert }: { alert: RiskFlag }) {
  const isSurvey = alert.sourceType === "survey";
  const isActivity = alert.sourceType === "activity";
  const uniqueCategories = [
    ...new Set(alert.categories?.map((c) => c.category) ?? []),
  ];
  const teen = alert.mentorship?.mentee ?? alert.user;
  const teenName = teen ? getMenteeDisplayName(teen) : "Unknown";

  const { data: activityEntity } = useShareableEntity(
    isActivity ? alert.sourceId : "",
    alert.mentorship?.mentee?.id,
  );
  const activitySubtype = activityEntity?.activity?.activityType;
  const typeLabel = isActivity
    ? activitySubtype && ACTIVITY_TYPE_LABELS[activitySubtype]
      ? `Activity · ${ACTIVITY_TYPE_LABELS[activitySubtype]}`
      : "Activity"
    : SOURCE_TYPE_LABELS[alert.sourceType] ?? alert.sourceType;

  return (
    <div className="flex flex-col gap-4">
      <DrawerField label="Flag ID">
        <span className="font-mono text-xs text-[#58534c]">{alert.id}</span>
      </DrawerField>
      <DrawerField label="Teen">
        {teen?.id ? (
          <Link
            to="/users/$userId"
            params={{ userId: teen.id }}
            className="text-[#1f1e1d] underline underline-offset-2 hover:text-black"
          >
            {teenName}
          </Link>
        ) : (
          teenName
        )}
      </DrawerField>
      {isSurvey ? (
        <DrawerField label="Source">
          <span className="inline-flex items-center rounded-full bg-[#f0edfb] px-2 py-1 text-sm text-[#5b4fa0]">
            Survey response
          </span>
        </DrawerField>
      ) : (
        <DrawerField label="Mentor">
          {alert.mentorship?.mentor?.id ? (
            <Link
              to="/users/$userId"
              params={{ userId: alert.mentorship.mentor.id }}
              className="text-[#1f1e1d] underline underline-offset-2 hover:text-black"
            >
              {alert.mentorship.mentor.name ?? "Unknown"}
            </Link>
          ) : (
            alert.mentorship?.mentor?.name ?? "Unknown"
          )}
        </DrawerField>
      )}
      {!isSurvey && (
        <DrawerField label="Type">
          <span className="inline-flex items-center rounded-full bg-[#ece8e1] px-2 py-1 text-sm text-[#1f1e1d]">
            {typeLabel}
          </span>
        </DrawerField>
      )}
      <DrawerField label="Priority">
        <PriorityPill priority={alert.priority} />
      </DrawerField>
      {uniqueCategories.length > 0 && (
        <DrawerField label="Categories">
          <div className="flex flex-wrap gap-1.5">
            {uniqueCategories.map((cat) => (
              <span
                key={cat}
                className="inline-flex items-center rounded-full bg-[#ece8e1] px-2 py-1 text-sm text-[#1f1e1d]"
              >
                {CATEGORY_LABELS[cat] ?? cat}
              </span>
            ))}
          </div>
        </DrawerField>
      )}
      <DrawerField label="Detected">
        {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
      </DrawerField>
    </div>
  );
}

// Activity voice recordings are known to be audio, so render a player directly
// from the S3 key rather than relying on extension sniffing.
function AudioPreview({ audioKey }: { audioKey: string }) {
  const { url: signedUrl, isPending } = useSignedMedia(audioKey);

  if (isPending) {
    return (
      <div className="flex h-10 items-center justify-center rounded-lg bg-zinc-50">
        <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!signedUrl) return null;

  return <MutingAudio src={signedUrl} controls className="w-full" />;
}

function MediaPreview({ url }: { url: string }) {
  const { url: signedUrl, isPending } = useSignedMedia(url);
  const mediaType = getMediaType(url);

  if (isPending) {
    return (
      <div className="flex h-24 items-center justify-center rounded-lg bg-zinc-50">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!signedUrl || !mediaType) return null;

  if (mediaType === "image") {
    return (
      <img
        src={signedUrl}
        alt="Flagged content"
        className="max-h-48 w-full rounded-lg object-contain"
      />
    );
  }

  if (mediaType === "video") {
    return (
      <MutingVideo
        src={signedUrl}
        controls
        className="max-h-48 w-full rounded-lg"
      >
        Your browser does not support the video tag.
      </MutingVideo>
    );
  }

  if (mediaType === "audio") {
    return <MutingAudio src={signedUrl} controls className="w-full" />;
  }

  return null;
}

function SurveyTriggers({ alert }: { alert: RiskFlag }) {
  const triggers =
    (alert.metadata?.triggers as string[] | undefined) ??
    (alert.metadata?.trigger ? [alert.metadata.trigger as string] : []);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-[#58534c]">Survey triggers</p>
      {triggers.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {triggers.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
              <p className="text-sm font-medium text-[#1f1e1d]">{t}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm italic text-[#58534c]">No trigger details</p>
      )}
    </div>
  );
}

const ENTITY_SOURCE_TYPES = ["note", "moment", "swap", "activity"];

function EntityPreview({ alert }: { alert: RiskFlag }) {
  const menteeId = alert.mentorship?.mentee?.id;
  const { data: entity, isPending } = useShareableEntity(
    alert.sourceId,
    menteeId,
  );

  if (isPending) {
    return (
      <div className="flex h-24 items-center justify-center rounded-lg bg-zinc-50">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!entity) {
    return (
      <p className="text-sm italic text-[#58534c]">Content not available</p>
    );
  }

  if (entity.entityType === "note") {
    return (
      <div className="flex items-center gap-2">
        <div className="w-0.5 self-stretch rounded-full bg-[#1f1e1d]" />
        <p className="text-sm leading-5 text-[#1f1e1d]">
          {entity.note?.content ? (
            `"${entity.note.content}"`
          ) : (
            <span className="italic text-[#58534c]">Empty note</span>
          )}
        </p>
      </div>
    );
  }

  if (entity.entityType === "moment") {
    return (
      <div className="flex flex-col gap-2">
        {entity.moment?.caption && (
          <p className="text-sm leading-5 text-[#1f1e1d]">
            {`"${entity.moment.caption}"`}
          </p>
        )}
        <div
          className="grid grid-cols-2 gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {(entity.moment?.images ?? []).map((img, i) => (
            <MediaPreview key={i} url={img.url} />
          ))}
        </div>
      </div>
    );
  }

  if (entity.entityType === "swap") {
    return (
      <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
        {entity.swap?.customTitle && (
          <p className="text-sm leading-5 text-[#1f1e1d]">
            {`"${entity.swap.customTitle}"`}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          {entity.swap?.mentorImage && (
            <div className="flex flex-col gap-1">
              <p className="text-xs text-[#58534c]">Mentor</p>
              <MediaPreview url={entity.swap.mentorImage.url} />
            </div>
          )}
          {entity.swap?.menteeImage && (
            <div className="flex flex-col gap-1">
              <p className="text-xs text-[#58534c]">Teen</p>
              <MediaPreview url={entity.swap.menteeImage.url} />
            </div>
          )}
        </div>
      </div>
    );
  }

  const activityType = entity.activity?.activityType;
  const activityLabel = activityType
    ? ACTIVITY_TYPE_LABELS[activityType] ?? activityType
    : "Activity";
  const scannedText = alert.metadata?.scanned_text as string | undefined;
  const audioKeys =
    (alert.metadata?.scanned_audio_keys as string[] | undefined) ?? [];
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-[#1f1e1d]">{activityLabel}</p>
      {audioKeys.length > 0 && (
        <div
          className="flex flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {audioKeys.map((key, i) => (
            <AudioPreview key={i} audioKey={key} />
          ))}
        </div>
      )}
      {scannedText && (
        <div className="flex items-center gap-2">
          <div className="w-0.5 self-stretch rounded-full bg-[#1f1e1d]" />
          <p className="text-sm leading-5 text-[#1f1e1d]">
            {`"${scannedText}"`}
          </p>
        </div>
      )}
      {audioKeys.length === 0 && !scannedText && (
        <p className="text-sm italic text-[#58534c]">
          No readable content captured
        </p>
      )}
    </div>
  );
}

function FlaggedQuote({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-0.5 self-stretch rounded-full bg-[#1f1e1d]" />
      <p className="text-sm leading-5 text-[#1f1e1d]">{`"${text}"`}</p>
    </div>
  );
}

function FlaggedMissing({ label }: { label: string }) {
  return (
    <p className="text-sm italic text-[#58534c]">
      Review the flagged {label} in their profile
    </p>
  );
}

function DrawerTextLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-fit items-center gap-1.5 text-xs text-[#58534c] transition-colors hover:text-[#1f1e1d]"
    >
      <ChevronRight className="h-3 w-3" />
      <span className="underline underline-offset-2">{label}</span>
    </button>
  );
}

function ProfilePreview({ alert }: { alert: RiskFlag }) {
  const field = (alert.metadata?.field as string | undefined) ?? "profile";
  const fieldLabel = PROFILE_FIELD_LABELS[field] ?? field;
  const flaggedImageUrl = alert.metadata?.flagged_image_url as
    | string
    | undefined;
  const content =
    flaggedImageUrl ?? alert.sourceContent ?? alert.transcribedText;
  const isMedia = !!flaggedImageUrl || (content ? isS3Url(content) : false);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-[#58534c]">
        Flagged profile field: {fieldLabel}
      </p>
      {content ? (
        isMedia ? (
          <div onClick={(e) => e.stopPropagation()}>
            <MediaPreview url={content} />
          </div>
        ) : (
          <FlaggedQuote text={content} />
        )
      ) : (
        <FlaggedMissing label={fieldLabel.toLowerCase()} />
      )}
    </div>
  );
}

function WallPreview({ alert }: { alert: RiskFlag }) {
  const navigate = useNavigate();
  const imageUrl = alert.metadata?.flagged_image_url as string | undefined;
  const prompt = alert.metadata?.flagged_prompt as string | undefined;
  const text = alert.metadata?.flagged_text as string | undefined;
  const itemLabel = alert.metadata?.avatar
    ? "profile picture"
    : alert.metadata?.prompt_id
      ? "prompt answer"
      : "photo";

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-[#58534c]">Flagged teen profile {itemLabel}</p>
      {imageUrl ? (
        <div onClick={(e) => e.stopPropagation()}>
          <MediaPreview url={imageUrl} />
        </div>
      ) : null}
      {prompt ? (
        <p className="text-xs italic text-[#58534c]">{prompt}</p>
      ) : null}
      {text ? <FlaggedQuote text={text} /> : null}
      {!imageUrl && !text ? <FlaggedMissing label={itemLabel} /> : null}
      {alert.mentorshipId ? (
        <DrawerTextLink
          label="View teen profile"
          onClick={() => {
            navigate({
              to: "/mentorships/$mentorshipId",
              params: { mentorshipId: alert.mentorshipId },
            });
          }}
        />
      ) : null}
    </div>
  );
}

function CallTranscript({
  onViewTranscript,
}: {
  onViewTranscript: () => void;
}) {
  return (
    <DrawerTextLink label="View call transcript" onClick={onViewTranscript} />
  );
}

export function FlaggedContent({
  alert,
  categories,
  onViewContext,
  onViewTranscript,
}: {
  alert: RiskFlag;
  categories: RiskFlagCategory[];
  onViewContext: () => void;
  onViewTranscript: () => void;
}) {
  const content = alert.sourceContent ?? alert.transcribedText;
  const isMedia = content ? isS3Url(content) : false;

  return (
    <div className="flex flex-col gap-4">
      {alert.sourceType === "survey" ? (
        <SurveyTriggers alert={alert} />
      ) : alert.sourceType === "profile" ? (
        <ProfilePreview alert={alert} />
      ) : alert.sourceType === "wall" ? (
        <WallPreview alert={alert} />
      ) : ENTITY_SOURCE_TYPES.includes(alert.sourceType) ? (
        <EntityPreview alert={alert} />
      ) : alert.sourceType === "video_call" ? (
        <CallTranscript onViewTranscript={onViewTranscript} />
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <p className="text-xs text-[#58534c]">Flagged content</p>
            </div>
            {isMedia ? (
              <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                <MediaPreview url={content!} />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-0.5 self-stretch rounded-full bg-[#1f1e1d]" />
                <p className="text-sm leading-5 tracking-[-0.15px] text-[#1f1e1d]">
                  {content ? (
                    `"${content}"`
                  ) : (
                    <span className="italic text-[#58534c]">
                      Content not available
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
          <DrawerTextLink label="View chat context" onClick={onViewContext} />
          {alert.transcribedText && isMedia && (
            <DrawerField label="Transcription">
              <p className="text-sm leading-5 tracking-[-0.15px] text-[#1f1e1d]">
                {`"${alert.transcribedText}"`}
              </p>
            </DrawerField>
          )}
        </>
      )}
      <DrawerField label="Why this was flagged">
        {categories.length === 1 ? (
          <p className="text-sm leading-5">{categories[0].explanation}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {categories.map((cat, i) => (
              <div key={i}>
                <p className="mb-0.5 text-xs font-medium text-[#58534c]">
                  {CATEGORY_LABELS[cat.category] ?? cat.category}
                </p>
                <p className="text-sm leading-5">{cat.explanation}</p>
              </div>
            ))}
          </div>
        )}
      </DrawerField>
    </div>
  );
}

export function ClosedFeedback({ alert }: { alert: RiskFlag }) {
  if (alert.resolutionReason === "expired") {
    return (
      <div className="flex flex-col gap-3.5">
        <DrawerField label="Resolution">
          <span className="text-sm text-zinc-500">
            Auto-expired after 7 days
          </span>
        </DrawerField>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <DrawerField label="Flagged correctly">
        {alert.feedbackCorrect === true
          ? "Yes"
          : alert.feedbackCorrect === false
            ? "No"
            : "—"}
      </DrawerField>
      {alert.feedbackNote && (
        <DrawerField label="Reviewer notes">{alert.feedbackNote}</DrawerField>
      )}
      {alert.closedByUser && (
        <DrawerField label="Reviewed by">{alert.closedByUser.name}</DrawerField>
      )}
    </div>
  );
}

export function FeedbackPrompt({
  onSelect,
  question = "Was this flagged correctly?",
}: {
  onSelect: (correct: boolean) => void;
  question?: string;
}) {
  return (
    <div className="flex flex-col gap-3.5">
      <p className="text-xs text-[#58534c]">{question}</p>
      <div className="flex gap-4">
        <button
          onClick={() => onSelect(false)}
          className="flex-1 rounded-[10px] border border-[#ece8e1] bg-white px-10 py-1.5 text-sm leading-5 tracking-[-0.15px] text-[#1f1e1d] hover:bg-neutral-50"
        >
          No
        </button>
        <button
          onClick={() => onSelect(true)}
          className="flex-1 rounded-[10px] bg-black px-10 py-1.5 text-sm leading-5 tracking-[-0.15px] text-white hover:bg-zinc-800"
        >
          Yes
        </button>
      </div>
    </div>
  );
}

export function ResolveForm({
  feedbackCorrect,
  feedbackNote,
  isPending,
  onChangeCorrect,
  onChangeNote,
  onResolve,
  correctLabel = "Flagged correctly",
  notePlaceholder = "eg. This was flagged correctly...",
}: {
  feedbackCorrect: boolean;
  feedbackNote: string;
  isPending: boolean;
  onChangeCorrect: (val: boolean | null) => void;
  onChangeNote: (val: string) => void;
  onResolve: () => void;
  correctLabel?: string;
  notePlaceholder?: string;
}) {
  return (
    <div className="flex flex-1 flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#58534c]">
          {correctLabel}:{" "}
          <span className="font-medium text-[#1f1e1d]">
            {feedbackCorrect ? "Yes" : "No"}
          </span>
        </p>
        <button
          onClick={() => onChangeCorrect(null)}
          className="text-xs text-[#58534c] underline hover:text-[#1f1e1d]"
        >
          Change
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-xs text-[#58534c]">Tell us more</p>
        <textarea
          value={feedbackNote}
          onChange={(e) => onChangeNote(e.target.value)}
          placeholder={notePlaceholder}
          className="h-[140px] w-full resize-none rounded-[10px] border border-[#ece8e1] bg-white px-4 py-3 text-sm leading-5 text-[#1f1e1d] placeholder:text-[#a09a93] focus:outline-none focus:ring-1 focus:ring-[#ece8e1]"
        />
      </div>
      <div className="flex justify-end">
        <button
          onClick={onResolve}
          disabled={isPending}
          className="w-[163.5px] rounded-[10px] bg-black py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {isPending ? "Resolving..." : "Resolve"}
        </button>
      </div>
    </div>
  );
}
